// Ayurveda.jsx
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import Lightbox from "yet-another-react-lightbox";
import Thumbnails from "yet-another-react-lightbox/plugins/thumbnails";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import "yet-another-react-lightbox/styles.css";
import "yet-another-react-lightbox/plugins/thumbnails.css";

// === IMAGES ===
// Use appropriate serene / Ayurvedic-themed images from your assets
import heroBg from '../assets/Ayurveda/IMG_1682.jpeg';          // Hero: beach + palms or treatment ambiance
import ayurveda1 from '../assets/Ayurveda/67.jpeg';
import ayurveda2 from '../assets/Ayurveda/65.jpeg';
import ayurveda3 from '../assets/Ayurveda/66.jpeg';
import ayurveda4 from '../assets/Ayurveda/39.jpeg';
import ayurveda5 from '../assets/Ayurveda/64.jpg';

// === TREATMENT IMAGES ===
import shirodharaImg from '../assets/Ayurveda/64.jpg';
import steamBathImg from '../assets/Ayurveda/20.jpeg';
import flowerBathImg from '../assets/Ayurveda/59.jpeg';
import headMassageImg from '../assets/Ayurveda/65.jpeg';
import faceMassageImg from '../assets/Ayurveda/44.jpeg';
import neckMassageImg from '../assets/Ayurveda/66.jpeg';
import shoulderMassageImg from '../assets/Ayurveda/69.jpeg';
import bodyMassageImg from '../assets/Ayurveda/IMG_1696.jpeg';
import backMassageImg from '../assets/Ayurveda/74.webp';
import footMassageImg from '../assets/Ayurveda/75.jpg';
import purgativeImg from '../assets/Ayurveda/78.webp';
import nasalClearingImg from '../assets/Ayurveda/76.jpg';
import powderMassageImg from '../assets/Ayurveda/77.webp';
import akkupancharImg from '../assets/Ayurveda/79.jpg';

// === NEW TREATMENT IMAGES ===
import intensiveMassageImg from '../assets/Ayurveda/80.jpg';
import thermoMassageImg from '../assets/Ayurveda/81.jpg';
import synchronMassageImg from '../assets/Ayurveda/82.jpg';
import specialMassageImg from '../assets/Ayurveda/83.jpg';

import benefitsImg from '../assets/Ayurveda/68.jpeg'; // Image for Benefits section

const cardVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: { opacity: 1, scale: 1, transition: { duration: 0.6 } },
};

const ayurvedaImages = [
    { src: ayurveda1, altKey: 'ayurveda1' },
    { src: ayurveda2, altKey: 'ayurveda2' },
    { src: ayurveda3, altKey: 'ayurveda3' },
    { src: ayurveda4, altKey: 'ayurveda4' },
    { src: ayurveda5, altKey: 'ayurveda5' },
];


// === MOTION VARIANTS ===
const sectionVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } },
};

const staggerContainer = {
    hidden: { opacity: 0 },
    show: {
        opacity: 1,
        transition: { staggerChildren: 0.2 },
    },
};

const accordionItem = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } },
};

const textVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: 'easeOut' } },
};

const scaleUp = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: { opacity: 1, scale: 1, transition: { duration: 0.8, ease: 'easeOut' } },
};

const Ayurveda = () => {
    const { t } = useTranslation('ayurveda');

    useEffect(() => {
        if (typeof window !== 'undefined') {
            window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
        }
    }, []);

    // Intro paragraphs (can move to i18n files later)
    const [activeIndex, setActiveIndex] = useState(null);
    const [index, setIndex] = useState(-1);


    const toggleService = (index) => {
        setActiveIndex(activeIndex === index ? null : index);
    };
    const dataArray = [
        {
            title: t('accordionTitle1'),
            desc: t('accordionDesc1')
        },
        {
            title: t('accordionTitle2'),
            desc: t('accordionDesc2')
        },
        {
            title: t('accordionTitle3'),
            desc: t('accordionDesc3')
        },
        {
            title: t('accordionTitle4'),
            desc: t('accordionDesc4')
        },
        {
            title: t('accordionTitle5'),
            desc: t('accordionDesc5')
        }
    ];

    const ayurvedaSliderSettings = {
        dots: false,
        infinite: true,
        speed: 800,
        slidesToShow: 1,
        slidesToScroll: 1,
        autoplay: true,
        autoplaySpeed: 4000,
        arrows: false,

        customPaging: () => (
            <div className="w-3 h-3 rounded-full bg-white hover:bg-[#BF943A] transition"></div>
        ),
    };

    const treatments = {
        shirodhara: {
            name: t('shirodhara'),
            description: t('shirodharaDesc'),
            benefits: [
                t('shirodhara1'),
                t('shirodhara2'),
                t('shirodhara3'),
            ],
            image: shirodharaImg,
        },
        steamBath: {
            name: t('steamBath'),
            description: t('steamBathDesc'),
            benefits: [
                t('steamBath1'),
                t('steamBath2'),
                t('steamBath3'),
            ],
            image: steamBathImg,
        },
        flowerBath: {
            name: t('flowerBath'),
            description: t('flowerBathDesc'),
            benefits: [
                t('flowerBath1'),
                t('flowerBath2'),
                t('flowerBath3'),
            ],
            image: flowerBathImg,
        },
        headMassage: {
            name: t('headMassage'),
            description: t('headMassageDesc'),
            benefits: [
                t('headMassage1'),
                t('headMassage2'),
                t('headMassage3'),
            ],
            image: headMassageImg,
        },
        faceMassage: {
            name: t('faceMassage'),
            description: t('faceMassageDesc'),
            benefits: [
                t('faceMassage1'),
                t('faceMassage2'),
                t('faceMassage3'),
            ],
            image: faceMassageImg,
        },
        neckMassage: {
            name: t('neckMassage'),
            description: t('neckMassageDesc'),
            benefits: [
                t('neckMassage1'),
                t('neckMassage2'),
                t('neckMassage3'),
            ],
            image: neckMassageImg,
        },
        shoulderMassage: {
            name: t('shoulderMassage'),
            description: t('shoulderMassageDesc'),
            benefits: [
                t('shoulderMassage1'),
                t('shoulderMassage2'),
                t('shoulderMassage3'),
            ],
            image: shoulderMassageImg,
        },
        bodyMassage: {
            name: t('bodyMassage'),
            description: t('bodyMassageDesc'),
            benefits: [
                t('bodyMassage1'),
                t('bodyMassage2'),
                t('bodyMassage3'),
            ],
            image: bodyMassageImg,
        },
        backMassage: {
            name: t('backMassage'),
            description: t('backMassageDesc'),
            benefits: [
                t('backMassage1'),
                t('backMassage2'),
                t('backMassage3'),
            ],
            image: backMassageImg,
        },
        footMassage: {
            name: t('footMassage'),
            description: t('footMassageDesc'),
            benefits: [
                t('footMassage1'),
                t('footMassage2'),
                t('footMassage3'),
            ],
            image: footMassageImg,
        },
        purgative: {
            name: t('purgative'),
            description: t('purgativeDesc'),
            benefits: [
                t('purgative1'),
                t('purgative2'),
                t('purgative3'),
            ],
            image: purgativeImg,
        },
        nasalClearing: {
            name: t('nasalClearing'),
            description: t('nasalClearingDesc'),
            benefits: [
                t('nasalClearing1'),
                t('nasalClearing2'),
                t('nasalClearing3'),
            ],
            image: nasalClearingImg,
        },
        powderMassage: {
            name: t('powderMassage'),
            description: t('powderMassageDesc'),
            benefits: [
                t('powderMassage1'),
                t('powderMassage2'),
                t('powderMassage3'),
            ],
            image: powderMassageImg,
        },
        intensiveMassage: {
            name: t('intensiveMassage'),
            description: t('intensiveMassageDesc'),
            benefits: [
                t('intensiveMassage1'),
                t('intensiveMassage2'),
                t('intensiveMassage3'),
            ],
            image: intensiveMassageImg,
        },
        thermoMassage: {
            name: t('thermoMassage'),
            description: t('thermoMassageDesc'),
            benefits: [
                t('thermoMassage1'),
                t('thermoMassage2'),
                t('thermoMassage3'),
            ],
            image: thermoMassageImg,
        },
        synchronMassage: {
            name: t('synchronMassage'),
            description: t('synchronMassageDesc'),
            benefits: [
                t('synchronMassage1'),
                t('synchronMassage2'),
                t('synchronMassage3'),
            ],
            image: synchronMassageImg,
        },
        specialMassage: {
            name: t('specialMassage'),
            description: t('specialMassageDesc'),
            benefits: [
                t('specialMassage1'),
                t('specialMassage2'),
                t('specialMassage3'),
            ],
            image: specialMassageImg,
        },
    };

    const treatmentsArray = Object.values(treatments);
    const benefitsDetails = [
        <>
            <span style={{ color: '#96BA33' }}>{t('benefit1Title')}</span> {t('benefit1Desc')}
        </>,
        <>
            <span style={{ color: '#96BA33' }}>{t('benefit2Title')}</span> {t('benefit2Desc')}
        </>,
        <>
            <span style={{ color: '#96BA33' }}>{t('benefit3Title')}</span> {t('benefit3Desc')}
        </>,
        <>
            <span style={{ color: '#96BA33' }}>{t('benefit4Title')}</span> {t('benefit4Desc')}
        </>,
        <>
            <span style={{ color: '#96BA33' }}>{t('benefit5Title')}</span> {t('benefit5Desc')}
        </>,
        <>
            <span style={{ color: '#96BA33' }}>{t('benefit6Title')}</span> {t('benefit6Desc')}
        </>,
    ];

    const ayurvedaGallery = [
        { image: shirodharaImg, label: t('gallery1'), span: "col-span-1 row-span-1" },
        { image: steamBathImg, label: t('gallery2'), span: "col-span-1 row-span-2" },
        { image: flowerBathImg, label: t('gallery3'), span: "col-span-1 row-span-1" },
        { image: headMassageImg, label: t('gallery4'), span: "col-span-1 row-span-2" },
        { image: bodyMassageImg, label: t('gallery5'), span: "col-span-1 row-span-2" },
        { image: neckMassageImg, label: t('gallery6'), span: "col-span-1 row-span-2" },
        { image: faceMassageImg, label: t('gallery7'), span: "col-span-1 row-span-1" },
        { image: shoulderMassageImg, label: t('gallery8'), span: "col-span-1 row-span-1" },
    ];

    const slides = ayurvedaGallery.map(item => ({ src: item.image, alt: item.label }));


    return (
        <div className="bg-[#1F1F1F] min-h-screen">
            {/* ===== HERO SECTION ===== */}
            <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={sectionVariants}
                className="relative flex h-[60vh] w-full items-center justify-center font-lato md:h-[70vh] lg:h-[85vh]"
            >
                <img
                    src={heroBg}
                    alt="Ayurveda & Wellness at Muthumuni Beach Resort"
                    className="absolute inset-0 h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-[#020202c7] via-[#00000080] to-[#42424259]" />

                <div className="container relative z-10 flex items-center justify-center pt-16 md:pt-20">
                    <div className="w-full max-w-4xl text-center">
                        <h1 className="text-5xl md:text-6xl lg:text-7xl font-semibold leading-tight text-white font-copperplate">
                            {t('heroTitle')}
                        </h1>
                        <p className="mt-6 text-lg md:text-2xl lg:text-3xl font-semibold tracking-wide text-[#96BA33] font-lato">
                            {t('heroSubtitle')}
                        </p>

                        {/* Decorative SVG lines */}
                        <div className="absolute left-8 top-10 hidden lg:block">
                            <svg width="289" height="245" viewBox="0 0 289 245" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M289 1H1V244.5" stroke="white" strokeWidth="2" />
                            </svg>
                        </div>
                        <div className="absolute -bottom-12 right-8 hidden lg:block">
                            <svg width="289" height="244" viewBox="0 0 289 244" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M0 243.5H288V0" stroke="white" strokeWidth="2" />
                            </svg>
                        </div>
                    </div>
                </div>
            </motion.div>
            {/* ===== AYURVEDA & WELLNESS SECTION ===== */}
            <section className="bg-[#1F1F1F] py-12">
                <div className="container font-redhat text-white">
                    <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={textVariants} className="flex items-center gap-5">
                        <div className="h-px w-[40px] bg-[#D7D7D7]"></div>
                        <h1 className="text-[15px] font-semibold uppercase md:text-[30px] lg:text-[40px]">
                            {t('sectionEyebrow')}
                        </h1>
                    </motion.div>

                    <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={textVariants} className="mt-2 justify-between lg:flex">
                        <div className="w-full font-semibold text-[#AEAEAE] lg:w-[50%] lg:text-[18px]">
                            <p className="mb-4">
                                {t('sectionDescription')}
                            </p>
                        </div>
                    </motion.div>

                    <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={textVariants} className="my-6 h-px w-full bg-[#D7D7D7] lg:my-12"></motion.div>

                    <div className="justify-between gap-14 lg:flex">
                        {/* Ayurveda Swiper */}
                        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={scaleUp} className="relative mb-10 h-[500px] w-full lg:mb-0 lg:h-[650px] lg:w-[40%] rounded-lg overflow-hidden">
                            <Slider {...ayurvedaSliderSettings}>
                                {ayurvedaImages.map((image, index) => (
                                    <div key={index} className="h-full">
                                        <img
                                            src={image.src}
                                            alt={t(`kandy.images.${image.altKey}`)}
                                            className="h-full w-full object-cover"
                                            onError={(e) => { e.target.src = 'https://via.placeholder.com/400x550?text=Image+Not+Found'; }}
                                        />
                                    </div>
                                ))}
                            </Slider>
                        </motion.div>

                        {/* Services Accordion */}
                        <motion.div
                            variants={staggerContainer}
                            initial="hidden"
                            whileInView="show"
                            viewport={{ once: true, amount: 0.2 }}
                            className="w-full space-y-4 lg:w-[60%]"
                        >
                            {dataArray.map((item, index) => (
                                <motion.div
                                    key={index}
                                    variants={accordionItem}
                                    className="cursor-pointer rounded-lg border border-gray-600 bg-[#1a1a1a] p-4 transition hover:border-white"
                                    onClick={() => toggleService(index)}
                                    role="button"
                                    aria-expanded={activeIndex === index}
                                    aria-controls={`service-${index}`}
                                >
                                    <h2 className="flex items-center justify-between text-[16px] font-semibold lg:text-[20px]">
                                        {item.title}
                                        <span className="text-[25px]">
                                            {activeIndex === index ? '−' : '+'}
                                        </span>
                                    </h2>
                                    {activeIndex === index && (
                                        <p id={`service-${index}`} className="mt-2 text-[14px] text-[#C2C2C2] lg:text-[16px] whitespace-pre-line">
                                            {item.desc}
                                        </p>
                                    )}
                                </motion.div>
                            ))}
                        </motion.div>
                    </div>
                </div>
            </section>
            {/* ================= TREATMENTS SECTION ================= */}
            <section className="container mx-auto px-4  bg-[#1F1F1F]">
                <motion.div
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.2 }}
                    variants={sectionVariants}
                    className="text-center mb-12"
                >
                    <h2 className="text-4xl md:text-5xl font-bold text-white">
                        <span className="bg-white bg-clip-text text-transparent">
                            {t('treatmentsTitle')}
                        </span>
                    </h2>
                    <p className="mt-4 text-lg text-[#96BA33] max-w-3xl mx-auto">
                        {t('treatmentsSubtitle')}
                    </p>
                </motion.div>

                {/* Cards grid – same layout as your packages */}
                <div
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.1 }}
                    variants={{
                        hidden: { opacity: 0 },
                        visible: { opacity: 1, transition: { staggerChildren: 0.15 } },
                    }}
                    className="grid gap-8 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
                >
                    {treatmentsArray.map((treatment) => (
                        <div
                            key={treatment.name}
                            variants={cardVariants}
                            className="group relative overflow-hidden rounded-2xl bg-gradient-to-b from-gray-900 to-gray-950 border border-gray-800 hover:border-[#96BA33]/60 transition-all duration-500 hover:shadow-xl hover:shadow-[#96BA33]/20 flex flex-col"
                        >
                            <div className="relative h-56 overflow-hidden">
                                <img
                                    src={treatment.image}
                                    alt={treatment.name}
                                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                            </div>

                            <div className="p-6 flex flex-col flex-grow">
                                <h3 className="text-2xl font-bold text-white mb-3">{treatment.name}</h3>
                                <p className="text-gray-300 text-sm mb-5 flex-grow">{treatment.description}</p>

                                <ul className="text-sm text-gray-400 space-y-1.5 mb-6">
                                    {treatment.benefits.map((benefit, i) => (
                                        <li key={i} className="flex items-start gap-2">
                                            <span className="text-[#96BA33] mt-1">•</span>
                                            {benefit}
                                        </li>
                                    ))}
                                </ul>


                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* ===== Benefits of Panchakarma Treatments===== */}
            <motion.section
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.25 }}
                variants={sectionVariants}
                className="bg-[#1F1F1F] py-16 md:py-24"
            >
                <div className="container mx-auto px-6 lg:px-8">
                    <h2 className="text-4xl md:text-5xl font-semibold text-center text-white mb-12 ">
                        {t('benefitsTitle')}
                    </h2>

                    <div className="grid md:grid-cols-2 gap-10 lg:gap-16 items-center">
                        {/* Image on LEFT */}
                        <div>
                            <img
                                src={benefitsImg} // ← Add your image import here, e.g. '../assets/Resort/bentota-river-safari.jpg'
                                alt="Benefits of Panchakarma Treatments"
                                className="w-full h-auto object-cover rounded-2xl shadow-2xl"
                                loading="lazy"
                            />
                        </div>

                        {/* Details on RIGHT */}
                        <div className="space-y-6 text-white text-lg leading-relaxed">
                            {benefitsDetails.map((point, i) => (
                                <p key={i} className="font-inknut">
                                    {point}
                                </p>
                            ))}

                        </div>
                    </div>
                </div>
            </motion.section>
            {/* ===== AKKUPANCHAR SECTION ===== */}
            <motion.section
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.25 }}
                variants={sectionVariants}
                className="bg-[#1F1F1F] py-16 md:py-24"
            >
                <div className="container mx-auto px-6 lg:px-8">
                    <h2 className="text-4xl md:text-5xl font-semibold text-center text-white mb-12">
                        {t('akkupancharTitle')}
                    </h2>

                    <div className="grid md:grid-cols-2 gap-10 lg:gap-16 items-center">
                        {/* Description on LEFT */}
                        <div className="space-y-6 text-white text-lg leading-relaxed order-2 md:order-1">
                            <p className="font-inknut">
                                <span style={{ color: '#96BA33' }}>Akkupanchar</span> {t('akkupancharDesc1')}
                            </p>
                            <p className="font-inknut">
                                <span style={{ color: '#96BA33' }}>{t('akkupancharBenefit')}</span> {t('akkupancharDesc2')}
                            </p>
                            <p className="font-inknut">
                                <span style={{ color: '#96BA33' }}>{t('akkupancharApplication')}</span> {t('akkupancharDesc3')}
                            </p>
                            <p className="font-inknut">
                                <span style={{ color: '#96BA33' }}>{t('akkupancharTransform')}</span> {t('akkupancharDesc4')}
                            </p>

                        </div>

                        {/* Image on RIGHT */}
                        <div className="order-1 md:order-2">
                            <img
                                src={akkupancharImg}
                                alt="Akkupanchar Therapy at Muthumuni Ayurveda Beach Resort"
                                className="w-full h-auto object-cover rounded-2xl shadow-2xl"
                                loading="lazy"
                            />
                        </div>
                    </div>
                </div>
            </motion.section>

            {/* ===== AYURVEDA TREATMENT PROGRAMME SECTION ===== */}
            <motion.section
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.25 }}
                variants={sectionVariants}
                className="bg-[#1F1F1F] py-16 md:py-24"
            >
                <div className="container mx-auto px-6 lg:px-8">
                    {/* Title & Subtitle */}
                    <div className="text-center mb-12">
                        <h2 className="text-4xl md:text-5xl font-semibold text-white mb-4">
                            AYURVEDA TREATMENT PROGRAMME
                        </h2>
                        <p className="text-2xl md:text-3xl font-semibold text-[#96BA33]">
                            Your Journey to Balance, Detox & Deep Renewal
                        </p>
                    </div>

                    {/* Introductory Paragraph */}
                    <p className="text-center text-white text-lg leading-relaxed max-w-4xl mx-auto mb-12 font-inknut">
                        Experience authentic Ayurvedic healing in a peaceful retreat designed to restore your body, calm your mind, and renew your energy. From the moment you arrive, our team takes care of every detail—so you can fully focus on your wellbeing.
                    </p>

                    {/* All Treatment Blocks – Centered Wrapper */}
                    <div className="max-w-4xl mx-auto space-y-12">
                        {/* 1. Welcome & Gentle Start */}
                        <div className="border-l-4 border-[#96BA33] pl-8">
                            <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
                                Welcome & Gentle Start
                            </h3>
                            <ul className="space-y-3 text-[#C2C2C2] text-lg font-inknut">
                                <li className="flex items-start gap-3">
                                    <span className="text-[#96BA33] mt-1">•</span>
                                    <span>Airport pick-up and transfer to the hotel</span>
                                </li>
                                <li className="flex items-start gap-3">
                                    <span className="text-[#96BA33] mt-1">•</span>
                                    <span>Warm welcome and hotel orientation</span>
                                </li>
                                <li className="flex items-start gap-3">
                                    <span className="text-[#96BA33] mt-1">•</span>
                                    <span>Introduction to your weekly wellness schedule</span>
                                </li>
                                <li className="flex items-start gap-3">
                                    <span className="text-[#96BA33] mt-1">•</span>
                                    <span>Relaxation Massage (Day 1) at the Ayurveda Centre to release travel fatigue and prepare your body for treatments</span>
                                </li>
                            </ul>
                        </div>

                        {/* 2. Doctor Consultation & Personal Plan */}
                        <div className="border-l-4 border-[#96BA33] pl-8">
                            <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
                                Doctor Consultation & Personal Plan
                            </h3>
                            <ul className="space-y-3 text-[#C2C2C2] text-lg font-inknut">
                                <li className="flex items-start gap-3">
                                    <span className="text-[#96BA33] mt-1">•</span>
                                    <span>Ayurvedic Doctor Consultation (Day 2 – morning)</span>
                                </li>
                                <li className="flex items-start gap-3">
                                    <span className="text-[#96BA33] mt-1">•</span>
                                    <span>Full assessment of your Dosha/body type and health concerns</span>
                                </li>
                                <li className="flex items-start gap-3">
                                    <span className="text-[#96BA33] mt-1">•</span>
                                    <span>Translator available upon request</span>
                                </li>
                                <li className="flex items-start gap-3">
                                    <span className="text-[#96BA33] mt-1">•</span>
                                    <span>Your doctor creates a personalised treatment plan tailored to your needs and goals</span>
                                </li>
                            </ul>
                        </div>

                        {/* 3. Daily Ayurveda Therapies */}
                        <div className="border-l-4 border-[#96BA33] pl-8">
                            <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
                                Daily Ayurveda Therapies
                            </h3>
                            <ul className="space-y-3 text-[#C2C2C2] text-lg font-inknut">
                                <li className="flex items-start gap-3">
                                    <span className="text-[#96BA33] mt-1">•</span>
                                    <span>4–5 treatments daily (as recommended by the doctor)</span>
                                </li>
                                <li className="flex items-start gap-3">
                                    <span className="text-[#96BA33] mt-1">•</span>
                                    <span>Therapies selected based on your body constitution and condition</span>
                                </li>
                                <li className="flex items-start gap-3">
                                    <span className="text-[#96BA33] mt-1">•</span>
                                    <span>Special massages may be added when required</span>
                                </li>
                                <li className="flex items-start gap-3">
                                    <span className="text-[#96BA33] mt-1">•</span>
                                    <span>Doctor monitors progress daily and may adjust treatments and herbal support to ensure the best results</span>
                                </li>
                                <li className="flex items-start gap-3">
                                    <span className="text-[#96BA33] mt-1">•</span>
                                    <span>If suitable, a gentle cleansing/purgative process may be recommended</span>
                                </li>
                            </ul>
                        </div>

                        {/* 4. Herbal Medicine Support */}
                        <div className="border-l-4 border-[#96BA33] pl-8">
                            <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
                                Herbal Medicine Support
                            </h3>
                            <ul className="space-y-3 text-[#C2C2C2] text-lg font-inknut">
                                <li className="flex items-start gap-3">
                                    <span className="text-[#96BA33] mt-1">•</span>
                                    <span>Doctor-prescribed herbal medicines served twice daily:</span>
                                </li>
                                <li className="flex items-start gap-3">
                                    <span className="text-[#96BA33] mt-1"></span>
                                    <span className="ml-8">during breakfast</span>
                                </li>
                                <li className="flex items-start gap-3">
                                    <span className="text-[#96BA33] mt-1"></span>
                                    <span className="ml-8">during dinner</span>
                                </li>
                                <li className="flex items-start gap-3">
                                    <span className="text-[#96BA33] mt-1">•</span>
                                    <span>Supporting internal healing, detoxification and long-lasting balance.</span>
                                </li>
                            </ul>
                        </div>

                        {/* 5. Yoga & Meditation for Mind–Body Harmony */}
                        <div className="border-l-4 border-[#96BA33] pl-8">
                            <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
                                Yoga & Meditation for Mind–Body Harmony
                            </h3>
                            <ul className="space-y-3 text-[#C2C2C2] text-lg font-inknut">
                                <li className="flex items-start gap-3">
                                    <span className="text-[#96BA33] mt-1">•</span>
                                    <span>Yoga sessions 4 times per week (approx. 1 hour)</span>
                                </li>
                                <li className="flex items-start gap-3">
                                    <span className="text-[#96BA33] mt-1">•</span>
                                    <span>Meditation sessions 2 times per week:</span>
                                </li>
                                <li className="flex items-start gap-3">
                                    <span className="text-[#96BA33] mt-1"></span>
                                    <span className="ml-8">a unique spiritual experience at an Island Temple, reached by boat</span>
                                </li>
                                <li className="flex items-start gap-3">
                                    <span className="text-[#96BA33] mt-1"></span>
                                    <span className="ml-8">guided meditation led by monks (approx. 1 hour)</span>
                                </li>
                            </ul>
                        </div>

                        {/* 6. Included Wellness Experiences */}
                        <div className="border-l-4 border-[#96BA33] pl-8">
                            <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
                                Included Wellness Experiences
                            </h3>
                            <ul className="space-y-3 text-[#C2C2C2] text-lg font-inknut">
                                <li className="flex items-start gap-3">
                                    <span className="text-[#96BA33] mt-1">•</span>
                                    <span>Weekly healthy cookery demonstrations</span>
                                </li>
                                <li className="flex items-start gap-3">
                                    <span className="text-[#96BA33] mt-1">•</span>
                                    <span>Free excursion (culture/nature experience)</span>
                                </li>
                            </ul>
                        </div>

                        {/* 7. Closing Ritual & Departure */}
                        <div className="border-l-4 border-[#96BA33] pl-8">
                            <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
                                Closing Ritual & Departure
                            </h3>
                            <ul className="space-y-3 text-[#C2C2C2] text-lg font-inknut">
                                <li className="flex items-start gap-3">
                                    <span className="text-[#96BA33] mt-1">•</span>
                                    <span>A beautiful Flower Bath (last day)—a traditional ritual of renewal</span>
                                </li>
                                <li className="flex items-start gap-3">
                                    <span className="text-[#96BA33] mt-1">•</span>
                                    <span>Final Doctor Consultation to review progress and provide:</span>
                                </li>
                                <li className="flex items-start gap-3">
                                    <span className="text-[#96BA33] mt-1"></span>
                                    <span className="ml-8">continued wellness guidance</span>
                                </li>
                                <li className="flex items-start gap-3">
                                    <span className="text-[#96BA33] mt-1"></span>
                                    <span className="ml-8">prescriptions and recommendations for home</span>
                                </li>
                                <li className="flex items-start gap-3">
                                    <span className="text-[#96BA33] mt-1">•</span>
                                    <span>Transfer back to the airport</span>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </motion.section>

            {/* AYURVEDA GALLERY SECTION */}
            <motion.section
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={sectionVariants}
                className="py-16 lg:py-24"
            >
                <div className="container mx-auto px-4">
                    {/* Grid */}
                    <div className="mt-8 grid auto-rows-[200px] grid-cols-2 gap-4 md:auto-rows-[260px] md:grid-cols-3 md:gap-5 lg:auto-rows-[480px] lg:gap-7 xl:gap-8">
                        {ayurvedaGallery.map((item, i) => (
                            <motion.button
                                key={i}
                                whileHover={{ scale: 1.05, y: -6 }}
                                whileTap={{ scale: 0.96 }}
                                type="button"
                                onClick={() => setIndex(i)}
                                className={`group relative overflow-hidden rounded-2xl shadow-2xl transition-all duration-500 ${item.span || ''}`}
                                aria-label={`Open ${i + 1}`}
                            >
                                <img
                                    src={item.image}
                                    alt={item.label}
                                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-115"
                                    loading="lazy"
                                />

                                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                                    <div className="absolute bottom-0 left-0 right-0 p-6 text-left">
                                        <h3 className="text-lg font-bold uppercase tracking-wider text-white drop-shadow-xl md:text-xl lg:text-2xl">
                                            {item.label}
                                        </h3>
                                    </div>
                                </div>
                            </motion.button>
                        ))}
                    </div>
                </div>

                {/* LIGHTBOX */}
                <Lightbox
                    open={index >= 0}
                    close={() => setIndex(-1)}
                    index={index}
                    slides={slides}
                    plugins={[Thumbnails, Zoom]}
                    thumbnails={{
                        position: "bottom",
                        width: 110,
                        height: 80,
                        border: 3,
                        borderColor: "transparent",
                        borderRadius: 12,
                        padding: 6,
                        gap: 16,
                        imageFit: "cover",
                    }}
                    zoom={{ maxZoomPixelRatio: 4 }}
                    styles={{
                        container: { backgroundColor: "rgba(10, 10, 10, 0.98)" },
                        thumbnail: {
                            cursor: "pointer",
                            background: "linear-gradient(45deg, #96BA33, #BF943A)",
                            borderRadius: "12px",
                            overflow: "hidden",
                            boxShadow: "0 4px 15px rgba(0,0,0,0.4)",
                        },
                        thumbnailsContainer: {
                            backgroundColor: "rgba(20,20,20,0.9)",
                            padding: "12px 0",
                            borderTop: "1px solid rgba(255,255,255,0.1)",
                        },
                    }}
                    labels={{
                        close: t('close') || "Close",
                        next: t('next') || "Next",
                        previous: t('previous') || "Previous",
                    }}
                    controller={{ closeOnBackdropClick: true }}
                />
            </motion.section>

        </div>
    );
};

export default Ayurveda;